import { Icon } from '@chakra-ui/react';
import { AssociationEntity, QuoteStatusEnum } from '@monetize/types/app';
import { useEffect, useRef, useState } from 'react';
import { IoMdSwap } from 'react-icons/io';
import { ExternalAssociationsIcons } from '../../../../components/ExternalAssociations/ExternalAssociationsIcons';
import {
  MBox,
  MButton,
  MDivider,
  MFlex,
} from '../../../../components/Monetize';
import { MBreadcrumb } from '../../../../components/Monetize/MBreadcrumb';
import { MPageTopNavContainer } from '../../../../components/Monetize/MPageTopNavContainer';
import { RecordAuditInfoPopover } from '../../../../components/Monetize/RecordAuditInfoPopover';
import { QuoteActionsV2 } from '../../../../components/Quotes/QuoteActionsV2';
import { QuoteHeaderSummary } from '../../../../components/Quotes/QuoteHeaderSummary';
import { QuoteProgressBar } from '../../../../components/Quotes/QuoteProgress/QuoteProgressBar';
import { QuoteTagsV2 } from '../../../../components/Quotes/QuoteTagsV2';
import { ROUTES } from '../../../../constants';
import { QUOTE_DESCRIPTION_EDITABLE_STATUSES } from '../../../../constants/quotes';
import { useAuth } from '../../../../services/auth0';
import { QuoteAlerts } from '../../Quote/QuoteAlerts';
import { useQuoteContext } from '../../Quote/quoteContext';
import { QuoteActionButtons } from './QuoteButtons/QuoteActionButtons';
import { QuoteEditButton } from './QuoteButtons/QuoteEditButton';
import { QuoteDescriptionEditor } from './QuoteDescriptionEditor';

interface QuotePageHeaderProps {
  hasValidRole: boolean;
  canEditQuote?: boolean;
  onOpenManualAcceptanceModal: () => void;
  onHeaderHeightChange?: (height: number) => void;
}

const DIVIDER = (
  <MDivider
    orientation="vertical"
    mx="3"
    borderColor="tBlack.200"
    height="28px"
  />
);
export const QuotePageHeader = ({
  hasValidRole,
  canEditQuote = false,
  onOpenManualAcceptanceModal,
  onHeaderHeightChange,
}: QuotePageHeaderProps) => {
  const {
    isReadOnly,
    isReadOnlyQuote,
    quoteData,
    quoteStateData,
    methods,
    reviewQuoteModalData,
    manualRenewalFormMethods,
    actionRunning,
    isEsignEnabled,
    isInternalView,
    handleSendQuote,
    handleRecreateQuote,
    handleUnExpireQuote,
    handleRecreateRenewalQuote,
    handleRecreateAmendmentQuote,
    setIsInternalView,
    resetQuoteForm,
  } = useQuoteContext();
  const {
    loading: quoteLoading,
    quote,
    setQuote,
    fetchQuote,
    updateQuote,
  } = quoteData;
  const isActionLoading =
    actionRunning || quoteLoading || quoteStateData.isReviewBtnPressed;

  const isDescriptionReadOnly =
    isReadOnlyQuote ||
    !QUOTE_DESCRIPTION_EDITABLE_STATUSES.has(quote?.status as QuoteStatusEnum);

  const { tenantId, userId } = useAuth();

  const [displaySticky, setDisplaySticky] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.addEventListener('scroll', handleStickyMenu);

    return () => {
      window.removeEventListener('scroll', handleStickyMenu);
    };
  }, []);

  useEffect(() => {
    if (!headerRef.current || !onHeaderHeightChange) {
      return;
    }

    const updateHeight = () => {
      if (headerRef.current) {
        onHeaderHeightChange(headerRef.current.offsetHeight);
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(headerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [onHeaderHeightChange]);

  const handleStickyMenu = () => {
    if (window !== undefined) {
      window.scrollY > 20 ? setDisplaySticky(true) : setDisplaySticky(false);
    }
  };

  if (!quote) {
    return null;
  }

  return (
    <MPageTopNavContainer
      ref={headerRef}
      position="sticky"
      top="0"
      background="tWhite.300"
      zIndex="4"
      boxShadow={displaySticky ? '22px 0px 56px 0px #00000021' : 'none'}
    >
      <MBox margin="0 auto">
        <MFlex
          alignItems="center"
          justifyContent="space-between"
          py={2}
          maxH="64px"
        >
          <MFlex alignItems="center" justifyContent="space-between">
            <MBreadcrumb
              items={[
                {
                  label: quote.accountName,
                  route: ROUTES.getAccountDetailRoute(quote.accountId),
                  linkProps: { lineClamp: 2 },
                },
                {
                  label:
                    quote?.status === QuoteStatusEnum.DRAFT
                      ? 'New Quote'
                      : 'Review Quote',
                  isCurrentPage: true,
                  renderItem: () => (
                    <QuoteDescriptionEditor
                      isReadOnly={isDescriptionReadOnly}
                      quote={quote}
                      setQuote={setQuote}
                      updateQuote={updateQuote}
                    />
                  ),
                },
              ]}
              hasBackButton
              containerProps={{ mr: '2', ml: -1 }}
            />
            <QuoteTagsV2
              quote={quote}
              isReadOnly={isReadOnly}
              setQuote={setQuote}
              manualRenewalFormMethods={manualRenewalFormMethods}
              mt={0.5}
            />
          </MFlex>
          {quote && tenantId && userId && (
            <MBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <MFlex alignItems="center" mr={4}>
                <RecordAuditInfoPopover
                  recordAuditInfo={quote}
                  tenantId={tenantId}
                  userId={userId}
                />
                <ExternalAssociationsIcons
                  entity={AssociationEntity.QUOTE}
                  recordId={quote.id}
                />
              </MFlex>
              <QuoteHeaderSummary />
            </MBox>
          )}
        </MFlex>
        <MFlex w="100%">
          <QuoteProgressBar
            quote={quote}
            approvals={quote.approvals}
            reloadData={() => {
              quote?.id && fetchQuote(quote.id);
            }}
            isLoading={quoteLoading}
          />
        </MFlex>
        <MFlex flexDir="row" justifyContent="flex-end" w="100%" py={2}>
          <MFlex align="center">
            {quote.validations.length > 0 && (
              <>
                <QuoteAlerts
                  validations={quote.validations}
                  showDivider={false}
                />
                {DIVIDER}
              </>
            )}

            {canEditQuote && <QuoteEditButton />}

            <MButton
              size="sm"
              variant="tertiary"
              minW="128px"
              mr={2}
              onClick={() => setIsInternalView(!isInternalView)}
            >
              {isInternalView
                ? 'Switch to External View'
                : 'Switch to Internal View'}
              <Icon ml="1" asChild>
                <IoMdSwap />
              </Icon>
            </MButton>

            <QuoteActionButtons
              isReadOnly={isReadOnly}
              isActionLoading={isActionLoading}
              isEsignEnabled={isEsignEnabled}
              quote={quote}
              quoteStateData={quoteStateData}
              quoteFormMethods={methods}
              reviewQuoteModalData={reviewQuoteModalData}
              setQuote={setQuote}
              onAcceptQuote={onOpenManualAcceptanceModal}
              onRecreateAmendmentQuote={handleRecreateAmendmentQuote}
              onRecreateRenewalQuote={handleRecreateRenewalQuote}
              onRecreateQuote={handleRecreateQuote}
              onUnExpireQuote={handleUnExpireQuote}
              onSendQuote={handleSendQuote}
            />

            <QuoteActionsV2
              quote={quote}
              setQuote={setQuote}
              loadData={() => quote.id && fetchQuote(quote.id)}
              manualRenewalFormMethods={manualRenewalFormMethods}
              resetQuoteForm={resetQuoteForm}
              onSendQuote={handleSendQuote}
            />
          </MFlex>
        </MFlex>
      </MBox>
    </MPageTopNavContainer>
  );
};
